import { useDraggable } from "@dnd-kit/core";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { SerializableBlockExport } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { isTemplateExport } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { Typography } from "@circle-react-uikit/Typography";
import { useToast } from "../../uikit/ToastV2";
import { LayoutBlock } from "../constants/BlockCategories";
import { BlockImagePreview } from "./BlockImagePreview";

const isStacksCategory = (block: SerializableBlockExport) =>
  block.meta.category?.includes(LayoutBlock);

export interface PreviewProps {
  block: SerializableBlockExport;
  isElementType?: boolean;
  isOverlay?: boolean;
  name: string;
  onAddBlock: () => void;
}

export const Preview = ({
  name,
  block,
  onAddBlock,
  isElementType = false,
  isOverlay = false,
}: PreviewProps) => {
  const dragAndDropType = useBuilderStore(state => state.dragAndDropType);
  const isDraggingEnabled = dragAndDropType !== "disabled";
  const toast = useToast();

  const id = isTemplateExport(block)
    ? block.templateId
    : block.defaultData.type;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: block,
    disabled: isOverlay,
  });

  const handleClick = () => {
    if (isElementType && !isStacksCategory(block)) {
      toast.show(
        t("builder_ui.errors.elements_should_be_added_in_layout_blocks"),
      );
      return;
    }

    if (!isDragging) {
      onAddBlock();
    }
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={handleClick}
      {...listeners}
      {...attributes}
      className={classNames(
        "border-primary bg-primary hover:border-dark hover:bg-secondary focus:border-dark focus:bg-secondary text-dark relative flex w-full flex-col items-center gap-2 rounded-lg border transition-colors duration-150 ease-linear",
        {
          "cursor-grab": isDraggingEnabled,
          "cursor-grabbing": isDragging,
          "cursor-pointer": !isDraggingEnabled && !isDragging,
          "opacity-75": isOverlay,
          "p-4": isElementType,
          "overflow-hidden": !isElementType,
        },
      )}
    >
      <div
        className={classNames("flex items-center justify-center", {
          "opacity-75": isOverlay,
        })}
      >
        <BlockImagePreview
          isElementType={isElementType}
          block={block}
          name={name}
        />
      </div>
      {isElementType && (
        <Typography.LabelXs color="text-dark">{name}</Typography.LabelXs>
      )}
    </button>
  );
};
